import { Datalayers } from '../Datalayers';
import { MODIFIERS, SELECTORS } from './config';
import { isValidEmail } from './helpers';

class ContactWizard {
    constructor(node) {
        this.node = node;
        this.steps = this.node.querySelector(SELECTORS.STEPS);
        this.stepItems = [...this.steps.querySelectorAll(SELECTORS.STEPS_ITEM)];
        this.totalSteps = this.stepItems.length - 1;
        this.form = this.node.querySelector('form');
        this.formSections = this.form ? [...this.form.querySelectorAll(SELECTORS.FORM_SECTION)] : null;
        this.back = this.form ? this.form.querySelector(SELECTORS.BACK) : null;
        this.submit = this.form ? this.form.querySelector(SELECTORS.SUBMIT) : null;
        this.innerSubmit = this.submit ? this.submit.querySelector('.btn__inner') : null;
        this.stepInput = this.form ? this.form.querySelector(SELECTORS.STEP_INPUT) : null;
        this.stepValid = false;
        this.buttonTranslation = this.innerSubmit?.textContent.replace(/[\n\r]+|[\s]{2,}/g, ' ');
        this.finalStepButtonTranslation = this.submit?.dataset.finalStepTranslation;
        this.submitTranslationChanged = false;

        this.getActiveNodes = this.getActiveNodes.bind(this);
        this.handleStepChange = this.handleStepChange.bind(this);
        this.saveProgress = this.saveProgress.bind(this);
        this.validate = this.validate.bind(this);

        this.init();
    }

    init() {
        this.getActiveStep();

        if (!(this.activeStep === this.totalSteps)) {
            this.getActiveNodes();
            this.validate();
            this.addEventListeners();
            this.handleSubmit();
            this.handleBackLink();
        } else {
            const datalayers = new Datalayers();
            datalayers.pushData({
                event: 'form',
                eventaction: 'submit',
                eventlabel: 'contactformulier',
            });
        }
    }

    addEventListeners() {
        this.activeNodes.fields.forEach((field) => {
            field.addEventListener('keyup', () => {
                this.validate();

                if (field.type === 'email') {
                    const isValid = isValidEmail(field.value);

                    if (isValid) {
                        field.parentNode.classList.remove(MODIFIERS.FORM_GROUP_ERROR);
                    }
                }
            });

            if (field.type === 'email') {
                field.addEventListener('blur', () => {
                    const isValid = isValidEmail(field.value);

                    if (!isValid) {
                        field.parentNode.classList.add(MODIFIERS.FORM_GROUP_ERROR);
                    }
                });
            }
        });
    }

    getActiveStep() {
        this.stepItems.forEach((item, i) => {
            if (item.dataset.state === 'active') {
                this.activeStep = i;
            }
        });
    }

    getActiveNodes() {
        this.activeNodes = {
            stepItem: this.stepItems[this.activeStep],
            formSection: this.formSections[this.activeStep],
            fields: Array.from(this.formSections[this.activeStep].elements),
        };
    }

    handleStepChange() {
        this.activeNodes.stepItem.dataset.state = this.lastStep < this.activeStep ? 'completed' : 'idle';
        this.activeNodes.formSection.classList.remove(MODIFIERS.FORM_SECTION);

        this.activeNodes = {};
        this.getActiveNodes();

        this.activeNodes.stepItem.dataset.state = 'active';
        this.activeNodes.formSection.classList.add(MODIFIERS.FORM_SECTION);

        if (this.activeStep > 0) {
            this.back.disabled = false;
            this.back.classList.add(MODIFIERS.BACK_LINK);
        } else {
            this.back.disabled = true;
            this.back.classList.remove(MODIFIERS.BACK_LINK);
        }

        if (this.activeStep === this.totalSteps - 1) {
            this.innerSubmit.textContent = this.finalStepButtonTranslation;
            this.submitTranslationChanged = true;
        } else if (this.submitTranslationChanged) {
            this.innerSubmit.textContent = this.buttonTranslation;
            this.submitTranslationChanged = false;
        }

        this.updateStepInputIndex(this.activeStep);

        this.validate();
        this.addEventListeners();
    }

    validate() {
        let isValid = false;
        const requiredFields = [];
        const emailFields = [];

        this.activeNodes.fields.forEach((field) => {
            if (field.required) {
                requiredFields.push(field);
            }

            if (field.type === 'email') {
                emailFields.push(field);
            }
        });

        const validInputs = requiredFields.filter((input) => input.value !== '');

        if (emailFields.length > 0) {
            const isValidEmailValue = (item) => isValidEmail(item.value);
            const allValid = emailFields.every(isValidEmailValue);

            isValid = allValid && requiredFields.length === validInputs.length;
        } else {
            isValid = requiredFields.length === validInputs.length;
        }

        this.submit.disabled = !isValid;
    }

    handleBackLink() {
        this.back.addEventListener('click', (e) => {
            e.preventDefault();

            this.lastStep = this.activeStep;
            this.activeStep -= 1;
            this.handleStepChange();
        });
    }

    handleSubmit() {
        this.form.addEventListener('submit', (e) => {
            if (this.activeStep !== this.totalSteps - 1) {
                e.preventDefault();

                this.lastStep = this.activeStep;
                this.activeStep += 1;

                this.handleStepChange();
            } else {
                // Change the value of this hidden field, because Drupal doesn't submit
                // if no changes are detected in the form fields
                this.updateStepInputIndex('final');
            }
        });
    }

    saveProgress() {
        this.data = new FormData(this.form);
        sessionStorage.setItem('contact_wizard_data', this.data);
        sessionStorage.setItem('contact_wizard_last_step', this.activeStep);
    }

    updateStepInputIndex(newStepIndex) {
        if (this.stepInput) {
            this.stepInput.value = newStepIndex;
        }
    }
}

export { ContactWizard };
